/**
 * ImportHelperMd.js
 * Instruction modal for import/export quiz questions
 * Displays AI prompt template for import and confirmation message for export
 */

import PropTypes from 'prop-types';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Button,
  VStack,
  Code,
  Link,
} from '@chakra-ui/react';

import { questionPromptTemplate } from 'utils/constants/question';

/**
 * ImportHelperMd component
 * @param {boolean} isOpen - Controls modal visibility
 * @param {function} onClose - Called when user cancels or clicks X
 * @param {function} onProceed - Called when user clicks Proceed - triggers import/export action
 * @param {string} actionType - Either 'import' or 'export'
 */
function ImportHelperMd({ isOpen, onClose, onProceed, actionType }) {
  const baseFileUrl = process.env.REACT_APP_FILE_URL;

  // Render content based on action type
  const renderContent = () => {
    if (actionType === 'import') {
      return (
        <VStack align="stretch" spacing={4}>
          {/* Template CSV download indicator */}
          <Alert status="info" borderRadius="md">
            <AlertIcon />
            <AlertDescription fontSize="sm">
              Not sure how to configure the file?{' '}
              <Link
                href={`${baseFileUrl}template/Quiz_Questions_Template.csv`}
                color="blue.500"
                fontWeight="medium"
                _hover={{ textDecoration: 'underline' }}
                download
              >
                Click here to download the sample CSV template.
              </Link>
            </AlertDescription>
          </Alert>

          <Text fontSize="md">
            Use the following AI prompt template to generate a properly
            formatted CSV file for quiz questions:
          </Text>

          <Box
            bg="gray.50"
            p={4}
            borderRadius="md"
            maxH="400px"
            overflowY="auto"
            _dark={{ bg: 'gray.800' }}
          >
            <Code
              display="block"
              whiteSpace="pre-wrap"
              bg="transparent"
              p={0}
              fontSize="sm"
            >
              {questionPromptTemplate}
            </Code>
          </Box>

          <Box>
            <Text fontSize="sm" fontWeight="medium" mb={2}>
              Skill Quest Wiki:
            </Text>
            <Link
              href="https://trendmicro.atlassian.net/wiki/spaces/SQK/pages/1494681927/1300.4+-+Quiz+Import+Export#HOW-TO-IMPORT-QUESTIONS"
              color="blue.500"
              fontSize="sm"
              _hover={{ textDecoration: 'underline' }}
              isExternal
            >
              How to Import Questions
            </Link>
          </Box>
        </VStack>
      );
    }

    if (actionType === 'export') {
      return (
        <VStack align="stretch" spacing={4}>
          <Text fontSize="md">
            You are about to export all questions to a CSV file. This file can
            be imported back into any quiz.
          </Text>
          <Text fontSize="sm" color="gray.600" _dark={{ color: 'gray.400' }}>
            The exported CSV will include all question data with proper
            formatting that can be used for backup or importing into other
            quizzes. You can view and read the wiki link provided below for more
            information regarding exporting quiz questions.
          </Text>
          <Box>
            <Text fontSize="sm" fontWeight="medium" mb={2}>
              Skill Quest Wiki:
            </Text>
            <Link
              href="https://trendmicro.atlassian.net/wiki/spaces/SQK/pages/1494681927/1300.4+-+Quiz+Import+Export#HOW-TO-EXPORT-QUESTIONS"
              color="blue.500"
              fontSize="sm"
              _hover={{ textDecoration: 'underline' }}
              isExternal
            >
              How to Export Questions
            </Link>
          </Box>
        </VStack>
      );
    }

    return null;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent maxW="800px">
        <ModalHeader>
          {actionType === 'import' ? 'Import Instructions' : 'Export Questions'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>{renderContent()}</ModalBody>
        <ModalFooter>
          <Button variant="outline" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme="red" onClick={onProceed}>
            Proceed
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

ImportHelperMd.defaultProps = {
  actionType: 'import',
};

ImportHelperMd.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onProceed: PropTypes.func.isRequired,
  actionType: PropTypes.oneOf(['import', 'export']),
};

export default ImportHelperMd;
